import * as THREE from 'three';
import {
  WALK_SPEED,
  RUN_SPEED,
  JUMP_FORCE,
  GRAVITY,
  GROUND_Y,
  CHAR_RADIUS,
} from '../config';
import type { InputState } from '../types';
import { CollisionWorld } from '../collision/CollisionWorld';
import { AnimationStateMachine } from './AnimationStateMachine';
import { smoothDamp, lerpAngle } from '../utils/math';

export class CharacterController {
  model: THREE.Group;
  private velocity = new THREE.Vector3();
  private grounded = true;
  private collisionWorld: CollisionWorld;
  private animState: AnimationStateMachine | null = null;
  private targetRotation = 0;

  // Exposed for camera
  position = new THREE.Vector3(0, 0, 0);

  constructor(model: THREE.Group, collisionWorld: CollisionWorld) {
    this.model = model;
    this.collisionWorld = collisionWorld;
    this.model.position.copy(this.position);
  }

  setAnimations(animState: AnimationStateMachine) {
    this.animState = animState;
  }

  update(delta: number, input: InputState, cameraYaw: number) {
    // ─── Movement direction relative to camera ───
    const moveX = input.moveX;
    const moveZ = input.moveZ;
    const hasInput = Math.abs(moveX) > 0.01 || Math.abs(moveZ) > 0.01;

    const speed = input.run ? RUN_SPEED : WALK_SPEED;

    if (hasInput) {
      // Convert input to world direction based on camera yaw
      const angle = Math.atan2(moveX, moveZ) + cameraYaw;
      const dirX = Math.sin(angle);
      const dirZ = Math.cos(angle);

      this.velocity.x = smoothDamp(this.velocity.x, dirX * speed, 10, delta);
      this.velocity.z = smoothDamp(this.velocity.z, dirZ * speed, 10, delta);

      // Face movement direction
      this.targetRotation = angle;
    } else {
      this.velocity.x = smoothDamp(this.velocity.x, 0, 10, delta);
      this.velocity.z = smoothDamp(this.velocity.z, 0, 10, delta);
    }

    // ─── Jump ───
    if (input.jump && this.grounded) {
      this.velocity.y = JUMP_FORCE;
      this.grounded = false;
    }

    // ─── Gravity ───
    this.velocity.y += GRAVITY * delta;

    // ─── Apply velocity ───
    this.position.x += this.velocity.x * delta;
    this.position.y += this.velocity.y * delta;
    this.position.z += this.velocity.z * delta;

    // ─── Ground check ───
    if (this.position.y <= GROUND_Y) {
      this.position.y = GROUND_Y;
      this.velocity.y = 0;
      this.grounded = true;
    }

    // ─── Collision resolution ───
    const resolved = this.collisionWorld.resolveCollision(
      this.position.x,
      this.position.y + 0.5, // sphere center offset
      this.position.z,
      CHAR_RADIUS
    );
    this.position.x = resolved.x;
    this.position.y = resolved.y - 0.5;
    this.position.z = resolved.z;
    if (resolved.grounded) this.grounded = true;

    // Re-check ground
    if (this.position.y < GROUND_Y) {
      this.position.y = GROUND_Y;
      this.velocity.y = 0;
      this.grounded = true;
    }

    // ─── Rotate model ───
    const currentRot = this.model.rotation.y;
    this.model.rotation.y = lerpAngle(currentRot, this.targetRotation, 1 - Math.exp(-15 * delta));

    // ─── Apply position ───
    this.model.position.copy(this.position);

    // ─── Update animations ───
    const horizontalSpeed = Math.sqrt(this.velocity.x ** 2 + this.velocity.z ** 2);
    this.animState?.update(delta, horizontalSpeed, this.velocity.y, this.grounded);
  }

  isGrounded(): boolean {
    return this.grounded;
  }
}
